import React, { lazy } from 'react';
import { Play } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { useSeo } from '../lib/useSeo';
import { QuoteBlock } from '../components/QuoteBlock';
export function Meditation() {
  useSeo({
    title: 'Meditation',
    description: 'Explore pathways to attain blissful peace of mind. Learn to let go of the old, appreciate the power of thoughts, and be open to change.',
    path: '/meditation',
    image: 'https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?auto=format&fit=crop&q=80&w=2000'
  });
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-338">
      <Header data-id="element-339" />

      <section className="relative h-[65vh] min-h-[460px] flex items-center justify-center overflow-hidden" data-id="element-340">
        <div className="absolute inset-0 z-0" data-id="element-341">
          <img src="https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?auto=format&fit=crop&q=80&w=2000" alt="Butterfly soft light" className="w-full h-full object-cover" data-id="element-342" />
          <div className="absolute inset-0 bg-white/50" data-id="element-343"></div>
        </div>
        <div className="relative z-10 text-center px-6 mt-16" data-id="element-344">
          <h1 className="font-serif text-charcoal drop-shadow-sm" data-id="element-345">
            <span className="block text-6xl md:text-7xl mb-2" data-id="element-346">Meditation</span>
            <span className="block text-xl md:text-2xl font-serif text-rose-700" data-id="element-347">
              made by Milangela's Soul Garden
            </span>
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-348">
          <WavyDivider fill="#EAE3D9" data-id="element-349" />
        </div>
      </section>

      <section className="bg-[#EAE3D9] py-20 px-6" data-id="element-350">
        <div className="max-w-4xl mx-auto mb-16" data-id="element-351">
          <a href="https://www.youtube.com/watch?v=87JRrd0RlTg" target="_blank" rel="noopener noreferrer" className="group relative block aspect-video rounded-2xl overflow-hidden shadow-soft bg-charcoal" data-id="element-352">
            <img src="https://img.youtube.com/vi/87JRrd0RlTg/maxresdefault.jpg" alt="Meditation to restore your energy" loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" data-id="element-353" />
            <div className="absolute inset-0 bg-charcoal/30 flex items-center justify-center" data-id="element-354">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-355">
                <Play size={26} fill="#3A3A38" className="text-charcoal ml-1" data-id="element-356" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 to-transparent" data-id="element-357">
              <p className="text-white text-sm font-medium tracking-wide drop-shadow" data-id="element-358">
                Meditation to restore your energy — Milangela's meditation
              </p>
            </div>
          </a>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-359">
          <div className="text-charcoal-50 font-light leading-relaxed space-y-5 text-sm md:text-justify" data-id="element-360">
            <p data-id="element-361">
              The benefits of meditation are manifold, and so are the types. I
              am exploring my own pathways to attain the blissful peace of mind
              and numbness of the body. Can we achieve a no-thought state, and
              simply be, breathing and feeling the collective wisdom? How to
              regain responsiveness to the energies?
            </p>
            <p data-id="element-362">To let go of the old that no longer serves us and:</p>
            <ul className="space-y-1.5" data-id="element-363">
              <li data-id="element-364">
                – understand the physical body is not who we are in essence,
              </li>
              <li data-id="element-365">– appreciate the power of our thoughts,</li>
              <li data-id="element-366">– be open to change.</li>
            </ul>
          </div>
          <div className="text-charcoal-50 font-light leading-relaxed text-sm md:text-justify" data-id="element-367">
            <p data-id="element-368">
              Our daily routines keep getting packed with things, and ticking
              off all the items on our "to-do list" is impossible. Let alone
              finding the time to sit down with a good book. So, I do hope you
              will find the thoughts and findings of relevant authors I will
              post to highlight my own ideas, guiding and motivating.
            </p>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative bg-[#EAE3D9] py-24 px-6 overflow-hidden" data-id="element-369">
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-370">
          <div className="text-6xl font-serif text-rose-500 leading-none mb-4" data-id="element-371">
            "
          </div>
          <p className="font-serif text-3xl md:text-4xl text-charcoal italic leading-snug mb-5" data-id="element-372">
            Silence isn't empty, it's full of answers.
          </p>
          <p className="text-charcoal-50 text-sm tracking-widest uppercase" data-id="element-373">
            — Unknown
          </p>
        </div>
      </section>
      <div className="relative bg-[#EAE3D9] pointer-events-none" data-id="element-374">
        <WavyDivider fill="#3A3A38" data-id="element-375" />
      </div>

      <Footer data-id="element-376" />
    </div>;
}